import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './Returns.css';

const ReturnRequestForm = ({ orderId, orderDetails }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [returnForm, setReturnForm] = useState({
    reason: '',
    type: 'refund',
    description: '',
    images: []
  });

  const returnReasons = [
    'Defective or damaged product',
    'Wrong item received',
    'Product not as described',
    'Size/fit issues',
    'Quality issues',
    'Changed mind',
    'Found better price elsewhere',
    'Other'
  ];

  const handleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setReturnForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitReturn = async (e) => {
    e.preventDefault();

    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to return');
      return;
    }

    if (!returnForm.reason) {
      toast.error('Please select a reason for return');
      return;
    }

    if (!returnForm.description.trim()) {
      toast.error('Please provide a detailed description');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const returnItems = orderDetails.items
        .filter(item => selectedItems.includes(item._id))
        .map(item => ({
          product: item.product._id || item.product,
          quantity: item.quantity,
          price: item.price
        }));

      const response = await axios.post(
        '/api/returns',
        {
          order: orderId,
          items: returnItems,
          reason: returnForm.reason,
          returnType: returnForm.type,
          description: returnForm.description,
          images: []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Return request submitted successfully!');
      navigate('/returns');
    } catch (error) {
      console.error('Error submitting return:', error);
      toast.error(error.response?.data?.message || 'Failed to submit return request');
    } finally {
      setLoading(false);
    }
  };

  const calculateRefundAmount = () => {
    if (!orderDetails || selectedItems.length === 0) return 0;

    return orderDetails.items
      .filter(item => selectedItems.includes(item._id))
      .reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  return (
    <div className="return-request-form">
      <div className="form-header">
        <h2>Request Return/Exchange</h2>
        <p>Order ID: #{orderId}</p>
      </div>

      <form onSubmit={handleSubmitReturn}>
        {/* Select Items */}
        <div className="form-section">
          <h3>Select Items to Return</h3>
          <div className="items-list">
            {orderDetails?.items?.map((item) => (
              <div
                key={item._id}
                className={`return-item ${selectedItems.includes(item._id) ? 'selected' : ''}`}
                onClick={() => handleItemSelection(item._id)}
              >
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item._id)}
                  onChange={() => handleItemSelection(item._id)}
                />
                <div className="item-image">
                  <img
                    src={item.product?.image || '/assets/no-image.svg'}
                    alt={item.product?.name}
                  />
                </div>
                <div className="item-details">
                  <h4>{item.product?.name}</h4>
                  <p>Quantity: {item.quantity}</p>
                  <p className="item-price">₹{item.price} each</p>
                </div>
                <div className="item-total">
                  ₹{item.price * item.quantity}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Return Type */}
        <div className="form-section">
          <h3>Return Type</h3>
          <div className="return-type-options">
            <label className={`type-option ${returnForm.type === 'refund' ? 'active' : ''}`}>
              <input
                type="radio"
                name="type"
                value="refund"
                checked={returnForm.type === 'refund'}
                onChange={handleFormChange}
              />
              <div className="option-content">
                <span className="icon">💰</span>
                <div>
                  <strong>Refund</strong>
                  <p>Get money back to your account</p>
                </div>
              </div>
            </label>

            <label className={`type-option ${returnForm.type === 'exchange' ? 'active' : ''}`}>
              <input
                type="radio"
                name="type"
                value="exchange"
                checked={returnForm.type === 'exchange'}
                onChange={handleFormChange}
              />
              <div className="option-content">
                <span className="icon">🔄</span>
                <div>
                  <strong>Exchange</strong>
                  <p>Replace with same or different product</p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Return Reason */}
        <div className="form-section">
          <h3>Reason for Return *</h3>
          <select
            name="reason"
            value={returnForm.reason}
            onChange={handleFormChange}
            required
          >
            <option value="">Select a reason</option>
            {returnReasons.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div className="form-section">
          <h3>Detailed Description *</h3>
          <textarea
            name="description"
            value={returnForm.description}
            onChange={handleFormChange}
            placeholder="Please provide details about the issue..."
            rows="5"
            maxLength="500"
            required
          ></textarea>
          <small>{returnForm.description.length}/500 characters</small>
        </div>

        {/* Refund Summary */}
        {selectedItems.length > 0 && (
          <div className="refund-summary">
            <h3>Return Summary</h3>
            <div className="summary-row">
              <span>Items selected:</span>
              <span>{selectedItems.length}</span>
            </div>
            <div className="summary-row">
              <span>Refund amount:</span>
              <span className="amount">₹{calculateRefundAmount()}</span>
            </div>
            <p className="summary-note">
              {returnForm.type === 'refund' 
                ? 'Refund will be processed within 5-7 business days after item verification'
                : 'Exchange item will be shipped after receiving and verifying returned item'}
            </p>
          </div>
        )}

        {/* Submit */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={loading || selectedItems.length === 0}
          >
            {loading ? 'Submitting...' : 'Submit Return Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

const ReturnsList = () => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchReturns();
  }, []);

  const fetchReturns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/returns/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReturns(response.data.returns || []);
    } catch (error) {
      console.error('Error fetching returns:', error);
      toast.error('Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      approved: '#2196f3',
      rejected: '#dc3545',
      picked_up: '#9c27b0',
      received: '#673ab7',
      inspected: '#3f51b5',
      refunded: '#28a745',
      completed: '#28a745'
    };
    return colors[status] || '#999';
  };

  const getStatusLabel = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const filteredReturns = returns.filter(ret => {
    if (filter === 'all') return true;
    return ret.status === filter;
  });

  if (loading) {
    return (
      <div className="returns-loading">
        <div className="spinner"></div>
        <p>Loading returns...</p>
      </div>
    );
  }

  return (
    <div className="returns-list-page">
      <div className="returns-header">
        <h2>My Returns & Exchanges</h2>
        <div className="filter-controls">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All ({returns.length})
          </button>
          <button
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={filter === 'approved' ? 'active' : ''}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button
            className={filter === 'completed' ? 'active' : ''}
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>
        </div>
      </div>

      {filteredReturns.length === 0 ? (
        <div className="no-returns">
          <p>No return requests found</p>
          <button onClick={() => navigate('/orders')}>View Orders</button>
        </div>
      ) : (
        <div className="returns-grid">
          {filteredReturns.map((returnRequest) => (
            <div key={returnRequest._id} className="return-card">
              <div className="return-card-header">
                <div className="return-id">
                  <span className="label">Return ID:</span>
                  <span className="value">#{returnRequest.returnNumber}</span>
                </div>
                <span
                  className="status-badge"
                  style={{ background: getStatusColor(returnRequest.status) }}
                >
                  {getStatusLabel(returnRequest.status)}
                </span>
              </div>

              <div className="return-info">
                <p><strong>Order ID:</strong> #{returnRequest.order?.orderNumber || returnRequest.order}</p>
                <p><strong>Type:</strong> {returnRequest.returnType.charAt(0).toUpperCase() + returnRequest.returnType.slice(1)}</p>
                <p><strong>Reason:</strong> {returnRequest.reason}</p>
                <p><strong>Requested on:</strong> {new Date(returnRequest.createdAt).toLocaleDateString()}</p>
              </div>

              <div className="return-items">
                <strong>Items ({returnRequest.items.length}):</strong>
                {returnRequest.items.map((item, index) => (
                  <div key={index} className="return-item-summary">
                    {item.product?.name || 'Product'} × {item.quantity}
                  </div>
                ))}
              </div>

              <div className="return-amount">
                <span>Refund Amount:</span>
                <span className="amount">₹{returnRequest.refundAmount}</span>
              </div>

              {returnRequest.pickupScheduled && returnRequest.pickupDetails && (
                <div className="pickup-info">
                  <p><strong>📦 Pickup Scheduled:</strong></p>
                  <p>{new Date(returnRequest.pickupDetails.date).toLocaleDateString()}</p>
                  <p>Time: {returnRequest.pickupDetails.timeSlot}</p>
                </div>
              )}

              {returnRequest.refundProcessed && returnRequest.refundDetails && (
                <div className="refund-info success">
                  <p><strong>✓ Refund Processed</strong></p>
                  <p>₹{returnRequest.refundDetails.amount} on {new Date(returnRequest.refundDetails.processedAt).toLocaleDateString()}</p>
                  <p>Method: {returnRequest.refundDetails.method}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { ReturnRequestForm, ReturnsList };
